import { MutableRefObject, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Dispatch } from "redux";
import { IOnboardingTooltipItem } from "../../models/onboarding-tooltip";
import { setVisibleTooltips } from "../../store/onboarding-tooltip/actions";
import { OnboardingTooltipActionTypes } from "../../store/onboarding-tooltip/types";
import {
  canViewDraftTooltips,
  useOnboardingTooltipMap,
  useOnboardingTooltipsEnabled
} from "./onboarding-tooltip-query";

// Coalesces bursts of DOM mutations (a panel mounting dozens of cards) into one scan.
const SCAN_DEBOUNCE_MS: number = 50;

// Collapse/expand animations briefly lay targets out at 0×0. One delayed re-measure picks them
// up once the animation has settled, without polling.
const ZERO_SIZE_RESCAN_MS: number = 250;

const SCAN_BATCH_SIZE: number = 50;

type TooltipMapGetter = () => Map<string, IOnboardingTooltipItem>;

type VisibleTooltipsSink = (visibleTooltips: Map<string, HTMLElement>) => void;

const toCssClassSelector = (selector: string): string => (selector.startsWith(".") ? selector : `.${selector}`);

const isValidSelector = (selector: string): boolean => {
  try {
    document.createDocumentFragment().querySelector(selector);
    return true;
  } catch {
    return false;
  }
};

export const findOnboardingTarget = (selector: string, tooltip: IOnboardingTooltipItem): HTMLElement | null => {
  if (tooltip.useCssSelector) {
    const cssSelector: string = toCssClassSelector(selector);
    if (!isValidSelector(cssSelector)) {
      console.warn(`Invalid CSS selector: ${selector}`);
      return null;
    }
    return document.querySelector<HTMLElement>(cssSelector);
  }
  return (
    document.querySelector<HTMLElement>(`[data-onboarding-id="${selector}"]`) ??
    document.querySelector<HTMLElement>(`[id="${selector}"]`)
  );
};

// Finds which tooltip targets are on screen and publishes them to the store. Owns the DOM
// observers; lives outside React so its lifecycle follows the route (start/stop), not mount.
export class OnboardingTooltipDomScanner {
  private readonly getTooltipMap: TooltipMapGetter;

  private readonly publish: VisibleTooltipsSink;

  private mutationObserver: MutationObserver | null = null;

  private intersectionObserver: IntersectionObserver | null = null;

  private observedElements: WeakSet<HTMLElement> = new WeakSet<HTMLElement>();

  private scanTimer: ReturnType<typeof setTimeout> | null = null;

  private rescanTimer: ReturnType<typeof setTimeout> | null = null;

  private batchFrame: number | null = null;

  private lastVisibleSelectors: Set<string> = new Set<string>();

  private relevantSelector: string = "[data-onboarding-id]";

  constructor(getTooltipMap: TooltipMapGetter, publish: VisibleTooltipsSink) {
    this.getTooltipMap = getTooltipMap;
    this.publish = publish;
  }

  get isRunning(): boolean {
    return this.mutationObserver !== null;
  }

  start(): void {
    if (this.isRunning) {
      return;
    }
    this.intersectionObserver = new IntersectionObserver(this.handleIntersections, {
      threshold: 0,
      rootMargin: "50px"
    });
    this.mutationObserver = new MutationObserver(this.handleMutations);
    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-onboarding-id"]
    });
    this.scheduleScan();
  }

  stop(): void {
    this.mutationObserver?.disconnect();
    this.mutationObserver = null;
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = null;
    this.observedElements = new WeakSet<HTMLElement>();
    this.clearTimers();
    if (this.lastVisibleSelectors.size > 0) {
      this.lastVisibleSelectors = new Set<string>();
      this.publish(new Map<string, HTMLElement>());
    }
  }

  // Recomputes the selector used to decide whether an added subtree can hold a target.
  onTooltipMapChanged(): void {
    const classSelectors: string[] = [];
    this.getTooltipMap().forEach((tooltip: IOnboardingTooltipItem, selector: string): void => {
      if (tooltip.useCssSelector) {
        const cssSelector: string = toCssClassSelector(selector);
        if (isValidSelector(cssSelector)) {
          classSelectors.push(cssSelector);
        }
      }
    });
    this.relevantSelector = ["[data-onboarding-id]", ...classSelectors].join(",");
    this.scheduleScan();
  }

  scheduleScan = (): void => {
    if (!this.isRunning) {
      return;
    }
    if (this.scanTimer) {
      clearTimeout(this.scanTimer);
    }
    this.scanTimer = setTimeout((): void => {
      this.scanTimer = null;
      this.scan(false);
    }, SCAN_DEBOUNCE_MS);
  };

  private clearTimers(): void {
    if (this.scanTimer) {
      clearTimeout(this.scanTimer);
      this.scanTimer = null;
    }
    if (this.rescanTimer) {
      clearTimeout(this.rescanTimer);
      this.rescanTimer = null;
    }
    if (this.batchFrame !== null) {
      cancelAnimationFrame(this.batchFrame);
      this.batchFrame = null;
    }
  }

  // An added node matters when it, or anything inside it, can be a target. Checking only the
  // root missed lazily rendered subtrees such as an expanded Elements-panel category.
  private containsTarget(node: Node): boolean {
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return false;
    }
    const element: Element = node as Element;
    return element.matches(this.relevantSelector) || element.querySelector(this.relevantSelector) !== null;
  }

  private handleMutations = (mutations: MutationRecord[]): void => {
    const shouldScan: boolean = mutations.some(
      (mutation: MutationRecord): boolean =>
        mutation.type === "attributes" ||
        Array.from(mutation.addedNodes).some((node: Node): boolean => this.containsTarget(node))
    );
    if (shouldScan) {
      this.scheduleScan();
    }
  };

  private handleIntersections = (entries: IntersectionObserverEntry[]): void => {
    const hasChanges: boolean = entries.some((entry: IntersectionObserverEntry): boolean => {
      const selector: string | null = (entry.target as HTMLElement).getAttribute("data-onboarding-id");
      if (!selector) {
        return true;
      }
      return this.lastVisibleSelectors.has(selector) !== entry.isIntersecting;
    });
    if (hasChanges) {
      this.scheduleScan();
    }
  };

  private scan(isRescan: boolean): void {
    const tooltipMap: Map<string, IOnboardingTooltipItem> = this.getTooltipMap();
    if (this.batchFrame !== null) {
      cancelAnimationFrame(this.batchFrame);
      this.batchFrame = null;
    }
    if (tooltipMap.size === 0) {
      this.commit(new Map<string, HTMLElement>());
      return;
    }

    const includeDrafts: boolean = canViewDraftTooltips();
    const selectors: string[] = Array.from(tooltipMap.keys()).filter(
      (selector: string): boolean => includeDrafts || !tooltipMap.get(selector)?.isDraft
    );
    const visible: Map<string, HTMLElement> = new Map<string, HTMLElement>();
    let foundZeroSizeTarget: boolean = false;

    const processBatch = (startIndex: number): void => {
      const endIndex: number = Math.min(startIndex + SCAN_BATCH_SIZE, selectors.length);
      for (let i = startIndex; i < endIndex; i++) {
        const selector: string = selectors[i];
        const tooltip: IOnboardingTooltipItem | undefined = tooltipMap.get(selector);
        const element: HTMLElement | null = tooltip ? findOnboardingTarget(selector, tooltip) : null;
        if (!element) {
          continue;
        }
        if (this.intersectionObserver && !this.observedElements.has(element)) {
          this.intersectionObserver.observe(element);
          this.observedElements.add(element);
        }
        const rect: DOMRect = element.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          visible.set(selector, element);
        } else {
          foundZeroSizeTarget = true;
        }
      }

      if (endIndex < selectors.length) {
        this.batchFrame = requestAnimationFrame((): void => processBatch(endIndex));
        return;
      }
      this.batchFrame = null;
      this.commit(visible);
      if (foundZeroSizeTarget && !isRescan) {
        this.rescanTimer = setTimeout((): void => {
          this.rescanTimer = null;
          this.scan(true);
        }, ZERO_SIZE_RESCAN_MS);
      }
    };

    processBatch(0);
  }

  private commit(visible: Map<string, HTMLElement>): void {
    if (!this.isRunning) {
      return;
    }
    // Nothing on screen before or after: skip a dispatch that would only re-render the layer.
    if (visible.size === 0 && this.lastVisibleSelectors.size === 0) {
      return;
    }
    this.lastVisibleSelectors = new Set<string>(visible.keys());
    this.publish(visible);
  }
}

let activeScanner: OnboardingTooltipDomScanner | null = null;

// Lets panels that swap content without a DOM mutation we can see (tab switches, lazy panels)
// ask for a fresh scan.
export const triggerGlobalTooltipScan = (): void => {
  activeScanner?.scheduleScan();
};

export const GlobalOnboardingTooltipManager = (): null => {
  const dispatch: Dispatch<OnboardingTooltipActionTypes> = useDispatch<Dispatch<OnboardingTooltipActionTypes>>();
  const enabled: boolean = useOnboardingTooltipsEnabled();
  const tooltipMap: Map<string, IOnboardingTooltipItem> = useOnboardingTooltipMap();
  const tooltipMapRef: MutableRefObject<Map<string, IOnboardingTooltipItem>> =
    useRef<Map<string, IOnboardingTooltipItem>>(tooltipMap);
  const [scanner] = useState<OnboardingTooltipDomScanner>(
    (): OnboardingTooltipDomScanner =>
      new OnboardingTooltipDomScanner(
        (): Map<string, IOnboardingTooltipItem> => tooltipMapRef.current,
        (visibleTooltips: Map<string, HTMLElement>): void => {
          dispatch(setVisibleTooltips(visibleTooltips));
        }
      )
  );

  useEffect((): (() => void) | undefined => {
    if (!enabled) {
      return undefined;
    }
    scanner.start();
    activeScanner = scanner;
    return (): void => {
      scanner.stop();
      if (activeScanner === scanner) {
        activeScanner = null;
      }
    };
  }, [enabled, scanner]);

  useEffect((): void => {
    tooltipMapRef.current = tooltipMap;
    scanner.onTooltipMapChanged();
  }, [tooltipMap, scanner]);

  return null;
};
